import {
  buildMessageCreatorDraftKey,
  deleteMessageCreatorDraft,
  getMessageCreatorDraft,
} from '@/helpers/messageCreatorDraftStorage';

export const MESSAGE_CREATOR_LOGOUT_AFTER_DRAFT_SAVE_KEY = 'message-creator-logout-after-draft-save';

export type LogoutDraftPromptState = {
  draftKey: string;
  updatedAt: number;
  performLogout: () => void;
};

type BeginLogoutWithDraftCheckParams = {
  userId: string;
  churchId: string;
  scopeId: string;
  isImpersonating: boolean;
  performLogout: () => void;
};

type ResolveMessageCreatorDraftUserIdParams = {
  apiProfileSub?: string | null;
  oidcUserSub?: string | null;
  localUserSub?: string | null;
};

const trimToNonEmpty = (value?: string | null): string => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim();
};

export const resolveMessageCreatorDraftUserId = ({
  apiProfileSub,
  oidcUserSub,
  localUserSub,
}: ResolveMessageCreatorDraftUserIdParams): string => {
  return trimToNonEmpty(apiProfileSub) || trimToNonEmpty(oidcUserSub) || trimToNonEmpty(localUserSub);
};

type Listener = () => void;
type PrepareLogoutDraft = () => Promise<void>;

let promptState: LogoutDraftPromptState | null = null;
let prepareLogoutDraft: PrepareLogoutDraft | null = null;

export const setLogoutDraftPrepare = ({ prepare }: { prepare: PrepareLogoutDraft | null }): void => {
  prepareLogoutDraft = prepare;
};
const listeners = new Set<Listener>();

const notifyListeners = (): void => {
  listeners.forEach((listener) => {
    listener();
  });
};

export const subscribeLogoutDraftPrompt = ({ listener }: { listener: Listener }): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getLogoutDraftPrompt = (): LogoutDraftPromptState | null => promptState;

export const beginLogoutWithDraftCheck = async ({
  userId,
  churchId,
  scopeId,
  isImpersonating,
  performLogout,
}: BeginLogoutWithDraftCheckParams): Promise<void> => {
  if (!userId || !churchId || !scopeId) {
    performLogout();
    return;
  }

  const draftKey = buildMessageCreatorDraftKey({
    userId,
    churchId,
    scopeId,
    isImpersonating,
  });

  try {
    await prepareLogoutDraft?.();
  } catch {
    // Keep the last saved draft if the designer export fails.
  }

  const snapshot = await getMessageCreatorDraft({ key: draftKey });

  if (!snapshot) {
    performLogout();
    return;
  }

  promptState = {
    draftKey,
    updatedAt: snapshot.updatedAt,
    performLogout,
  };
  notifyListeners();
};

export const discardLogoutDraftAndContinue = async (): Promise<void> => {
  const currentPrompt = promptState;
  if (!currentPrompt) {
    return;
  }

  promptState = null;
  notifyListeners();
  await deleteMessageCreatorDraft({ key: currentPrompt.draftKey });
  currentPrompt.performLogout();
};

export const cancelLogoutDraftPrompt = (): void => {
  if (!promptState) {
    return;
  }

  promptState = null;
  notifyListeners();
};

const getSessionStorage = (): Storage | null => {
  try {
    if (typeof globalThis === 'undefined' || !('sessionStorage' in globalThis)) {
      return null;
    }

    return globalThis.sessionStorage;
  } catch {
    return null;
  }
};

export const setLogoutAfterDraftSaveIntent = (): void => {
  getSessionStorage()?.setItem(MESSAGE_CREATOR_LOGOUT_AFTER_DRAFT_SAVE_KEY, '1');
};

export const hasLogoutAfterDraftSaveIntent = (): boolean => {
  return getSessionStorage()?.getItem(MESSAGE_CREATOR_LOGOUT_AFTER_DRAFT_SAVE_KEY) === '1';
};

export const clearLogoutAfterDraftSaveIntent = (): void => {
  getSessionStorage()?.removeItem(MESSAGE_CREATOR_LOGOUT_AFTER_DRAFT_SAVE_KEY);
};

export const consumeLogoutAfterDraftSaveIntent = (): boolean => {
  if (!hasLogoutAfterDraftSaveIntent()) {
    return false;
  }

  clearLogoutAfterDraftSaveIntent();
  return true;
};

export const chooseSaveDraftBeforeLogout = (): void => {
  if (!promptState) {
    return;
  }

  setLogoutAfterDraftSaveIntent();
  promptState = null;
  notifyListeners();
};
